import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from '../../../componentes/contenedores/Card';
import GraficoBarra from '../../../componentes/graficos/Barras';
import GraficoLineal from '../../../componentes/graficos/Lineal2';
import GraficoCircular from '../../../componentes/graficos/Circular';
import FiltroFechaUI from "../../../componentes/metodos/FiltroFechaUI";
import useFechaFiltro from "../../../componentes/metodos/LogicaFiltro";
import Page from '../../../componentes/plantilla/Page';
import {
    AnalisisResumenKPICajas,
    AnalisisDesgloseMetodoCajas,
    AnalisisDesgloseGastosCajas,
    AnalisisTendenciaDiariaCajas,
    AnalisisEstadoCajas,
    AnalisisAuditoriaCajas
} from '../../../configuracion/apiUrls';
import { formatoMoneda } from "../../../configuracion/validaciones";
moment.locale("es");

const CajasAnalisis = () => {
    const [cargando, setCargando] = useState(false);
    const [chartDesgloseMetodo, setChartDesgloseMetodo] = useState(null);
    const [chartDesgloseGastos, setChartDesgloseGastos] = useState(null);
    const [chartTendencia, setChartTendencia] = useState(null);
    const [metricas, setMetricas] = useState(null);
    const [estadoCajas, setEstadoCajas] = useState(null);
    const [tablaAuditoria, setTablaAuditoria] = useState([]);
    const [tipoMetodo, setTipoMetodo] = useState('ventas');
    const [datosMetodosRaw, setDatosMetodosRaw] = useState(null);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    useEffect(() => {
        if (datosMetodosRaw && datosMetodosRaw[tipoMetodo]) {
            const d = datosMetodosRaw[tipoMetodo];
            setChartDesgloseMetodo({
                labels: ['Efectivo', 'Tarjeta', 'Transferencia'],
                data: [d.efectivo || 0, d.tarjeta || 0, d.transferencia || 0],
                colors: ['#1cc88a', '#4e73df', '#36b9cc'],
                titulo: `Distribución de ${tipoMetodo.charAt(0).toUpperCase() + tipoMetodo.slice(1)}`
            });
        } else {
            setChartDesgloseMetodo(null);
        }
    }, [tipoMetodo, datosMetodosRaw]);

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };

        try {
            const [resKpi, resMetodos, resGastos, resTendencia, resEstado, resAuditoria] = await Promise.all([
                AxiosPublico.get(AnalisisResumenKPICajas, { params }),
                AxiosPublico.get(AnalisisDesgloseMetodoCajas, { params }),
                AxiosPublico.get(AnalisisDesgloseGastosCajas, { params }),
                AxiosPublico.get(AnalisisTendenciaDiariaCajas, { params }),
                AxiosPublico.get(AnalisisEstadoCajas, { params }),
                AxiosPublico.get(AnalisisAuditoriaCajas, { params })
            ]);

            setMetricas(resKpi.data);
            setEstadoCajas(resEstado.data.metricas);
            setTablaAuditoria(resAuditoria.data);

            // Guardamos la respuesta completa (ventas, compras, otros)
            setDatosMetodosRaw(resMetodos.data);

            // Desglose de Gastos
            if (resGastos.data && Object.keys(resGastos.data).length > 0) {
                const categorias = Object.keys(resGastos.data);
                const montos = Object.values(resGastos.data);

                setChartDesgloseGastos({
                    labels: categorias,
                    data: montos,
                    colors: ['#e74a3b', '#f6c23e', '#855fa8', '#5a5c69', '#36b9cc'],
                    titulo: "Gastos por Categoría"
                });
            } else {
                setChartDesgloseGastos(null);
            }

            // Tendencia Diaria
            if (resTendencia.data && Object.keys(resTendencia.data).length > 0) {

                const data = resTendencia.data;

                const fechasOrdenadas = Object.keys(data)
                    .sort((a, b) => new Date(a) - new Date(b));

                setChartTendencia({
                    labels: fechasOrdenadas.map(f =>
                        moment(f, "YYYY-MM-DD").format("DD/MM")
                    ),

                    datasets: [
                        {
                            label: "Ventas",
                            data: fechasOrdenadas.map(f =>
                                Number(data[f].pagosVentas || 0)
                            ),
                            borderColor: "#1cc88a",
                            fill: false
                        },
                        {
                            label: "Compras",
                            data: fechasOrdenadas.map(f =>
                                Number(data[f].pagosCompras || 0)
                            ),
                            borderColor: "#e74a3b",
                            fill: false
                        },
                        {
                            label: "Gastos Operativos",
                            data: fechasOrdenadas.map(f =>
                                Number(data[f].gastos || 0)
                            ),
                            borderColor: "#f6c23e",
                            fill: false
                        }
                    ]
                });

            } else {
                setChartTendencia(null);
            }


        } catch (error) {
            console.error("Error al cargar datos:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    const datos = {
        titulo:
        {
            titulo: "Análisis de Cajas",
            url: "/app/admin/analisis/cajas",
            tituloUrl: "Reportes",
            nombreUrl: "Cajas"
        },
    };

    return (
        <Page datos={datos}>
            <Card>
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
                <hr />
                {cargando ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                    <>
                        <div className="row mb-4">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-success border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Ingresos (Cobros)</h6>
                                    <h3 className="fw-bold mb-0">
                                        {formatoMoneda(metricas?.resumen?.ingresos?.total || 0)}
                                    </h3>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-danger border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Egresos (Pagos)</h6>
                                    <h3 className="fw-bold mb-0">
                                        {formatoMoneda(metricas?.resumen?.egresos?.total || 0)}
                                    </h3>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-primary border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Balance en Caja</h6>
                                    <h3 className={`fw-bold mb-0 ${metricas?.balanceNeto < 0 ? 'text-danger' : 'text-primary'}`}>
                                        {formatoMoneda(metricas?.balanceNeto || 0)}
                                    </h3>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-info border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Total Facturado</h6>
                                    <h3 className="fw-bold mb-0">
                                        {formatoMoneda(metricas?.resumen?.metrica?.totalVendido || 0)}
                                    </h3>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-5">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="fw-bold m-0">Distribución por Método</h6>
                                    <select
                                        className="form-select form-select-sm w-auto"
                                        value={tipoMetodo}
                                        onChange={(e) => setTipoMetodo(e.target.value)}
                                    >
                                        <option value="ventas">Ventas</option>
                                        <option value="compras">Compras</option>
                                        <option value="otros">Otros</option>
                                    </select>
                                </div>
                                {chartDesgloseMetodo ? (
                                    <GraficoCircular datos={chartDesgloseMetodo} />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <small className="text-muted">Sin datos de métodos de pago</small>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-7">
                                <h6 className="fw-bold text-center">Tendencia de Ingresos</h6>
                                {chartTendencia ? (
                                    <GraficoLineal datos={chartTendencia} />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <small className="text-muted">No hay datos de tendencia disponibles</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-4">
                            <div className="col-md-12">
                                <h6 className="fw-bold text-center">Desglose de Gastos</h6>
                                {chartDesgloseGastos ? (
                                    <GraficoBarra datos={chartDesgloseGastos} />
                                ) : (
                                    <div className="text-center py-5 bg-light rounded">
                                        <small className="text-muted">Sin datos de gastos</small>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="row mb-4 text-center">
                            <div className="col-md-3">
                                <small className="text-muted">Cajas Operadas</small>
                                <h5>{estadoCajas?.cajasOperadas} / {estadoCajas?.totalCajasSistema}</h5>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted">Cierres Realizados</small>
                                <h5>{estadoCajas?.cierresRealizados}</h5>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted">Cierres Pendientes</small>
                                <h5 className="text-warning">{estadoCajas?.cierresPendientes}</h5>
                            </div>
                            <div className="col-md-3">
                                <small className="text-muted">Fondo Fijo</small>
                                <h5 className="text-success">{formatoMoneda(estadoCajas?.fondoFijoTotal || 0)}</h5>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <h6 className="fw-bold">Auditoría de Diferencias</h6>
                                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                    <table className="table table-sm table-hover border">
                                        <thead className="bg-light sticky-top">
                                            <tr>
                                                <th>ID Cierre</th>
                                                <th>Caja</th>
                                                <th>Estado</th>
                                                <th className="text-end">Diferencia</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tablaAuditoria.map((item) => (
                                                <tr key={item.id}>
                                                    <td>#{item.id}</td>
                                                    <td>{item.caja}</td>
                                                    <td>
                                                        <span className={`badge ${item.estado === 'Sobrante' ? 'bg-info' : item.estado === 'Faltante' ? 'bg-danger' : 'bg-success'}`}>
                                                            {item.estado}
                                                        </span>
                                                    </td>
                                                    <td className={`text-end fw-bold ${item.diferencia < 0 ? 'text-danger' : 'text-dark'}`}>
                                                        {formatoMoneda(item.diferencia || 0)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </Page>
    );
}

export default CajasAnalisis;